/**
 * PatientLookupCard — PMS patient search + candidate confirmation for the
 * create-case page (AQ-580 v0, AQ-628/629).
 *
 * Behavior contract (PRD FR-1/FR-2):
 *  - last name mandatory (>=2 chars) unless a patient id is given
 *  - five response states: none / single / multiple / capped / error
 *  - candidates are ranked server-side; DOB is the verification attribute
 *  - confirming a candidate is the single act that populates anything (R7)
 *  - degraded mode never blocks intake
 */
import { type PmsCandidate, PmsResultState } from '@compass/client/types/pms';
import React, { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { usePmsPrefill } from '@/hooks/usePmsPrefill';

interface PatientLookupCardProps {
  pms: ReturnType<typeof usePmsPrefill>;
}

export function PatientLookupCard({ pms }: PatientLookupCardProps) {
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [dob, setDob] = useState('');
  const [patientId, setPatientId] = useState('');
  const [idMode, setIdMode] = useState(false);

  const canSearch = idMode ? patientId.trim().length > 0 : lastName.trim().length >= 2;

  const runSearch = () => {
    if (!canSearch) return;
    void pms.search(
      idMode
        ? { lastName: '', patientId: patientId.trim() }
        : {
            lastName: lastName.trim(),
            firstName: firstName.trim() || undefined,
            dob: dob || undefined,
          },
    );
  };

  if (pms.confirmedPatient) {
    return (
      <Card data-analytics-id="case:pms:lookup:confirmed">
        <CardContent className="flex items-center justify-between py-3">
          <div className="text-sm">
            <span className="font-medium">
              {pms.confirmedPatient.first_name} {pms.confirmedPatient.last_name}
            </span>{' '}
            <span className="text-muted-foreground">
              · DOB {pms.confirmedPatient.dob} · PMS ID {pms.confirmedPatient.mfi}
            </span>
            {pms.status === 'loading_prefill' && (
              <span className="ml-2 text-muted-foreground">Loading PMS record…</span>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={pms.reset}>
            Change patient
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-base">
          Patient Lookup (PMS)
          <button
            type="button"
            className="text-xs font-normal text-primary hover:underline"
            onClick={() => setIdMode(v => !v)}
          >
            {idMode ? 'Search by name instead' : 'Have a patient ID?'}
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {idMode ? (
          <div className="max-w-xs space-y-1">
            <Label htmlFor="pms-patient-id">Patient ID (MFI)</Label>
            <Input
              id="pms-patient-id"
              value={patientId}
              onChange={e => setPatientId(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && runSearch()}
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="pms-last-name">
                Last name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="pms-last-name"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && runSearch()}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pms-first-name">First name</Label>
              <Input
                id="pms-first-name"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && runSearch()}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pms-dob">Date of birth</Label>
              <Input id="pms-dob" type="date" value={dob} onChange={e => setDob(e.target.value)} />
            </div>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            disabled={!canSearch || pms.status === 'searching'}
            onClick={runSearch}
            data-analytics-id="case:pms:lookup:search"
          >
            {pms.status === 'searching' ? 'Searching…' : 'Search PMS'}
          </Button>
          {!idMode && lastName.trim().length > 0 && lastName.trim().length < 2 && (
            <span className="text-xs text-muted-foreground">
              Last name needs at least 2 characters
            </span>
          )}
        </div>

        {pms.status === 'error' && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm">
            PMS is temporarily unavailable — the form works as usual without it.
          </div>
        )}

        {pms.resultState === PmsResultState.PMS_RESULT_STATE_NONE && (
          <div className="rounded-md border p-3 text-sm">
            <p className="mb-2">No PMS match for this search.</p>
            <Button variant="outline" size="sm" onClick={pms.proceedWithoutPms}>
              Proceed without PMS
            </Button>
          </div>
        )}

        {pms.candidates.length > 0 && (
          <div className="space-y-2">
            {pms.resultState === PmsResultState.PMS_RESULT_STATE_CAPPED && (
              <p className="text-sm text-muted-foreground">
                {pms.totalMatches > 0 ? `${pms.totalMatches} matches` : 'More than 10 matches'} —
                add a date of birth or first name to narrow
                {pms.nextPageToken ? ', or show more' : ''}.
              </p>
            )}
            {pms.candidates.map(candidate => (
              <CandidateRow
                key={candidate.mfi}
                candidate={candidate}
                onConfirm={pms.confirmPatient}
              />
            ))}
            {pms.nextPageToken && (
              <Button
                variant="outline"
                size="sm"
                onClick={pms.showMore}
                disabled={pms.status === 'searching'}
              >
                Show more
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CandidateRow({
  candidate,
  onConfirm,
}: {
  candidate: PmsCandidate;
  onConfirm: (c: PmsCandidate) => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-md border p-3">
      <div className="text-sm">
        <div>
          <span className="font-semibold">DOB {candidate.dob || '—'}</span>
          <span className="mx-2 text-muted-foreground">·</span>
          <span>
            {candidate.first_name} {candidate.last_name}
          </span>
          {candidate.fuzzy_matched && (
            <Badge variant="outline" className="ml-2">
              close match
            </Badge>
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          PMS ID {candidate.mfi}
          {candidate.zip ? ` · ZIP ${candidate.zip}` : ''}
          {candidate.phone_last4 ? ` · phone …${candidate.phone_last4}` : ''}
        </div>
      </div>
      <Button
        size="sm"
        onClick={() => onConfirm(candidate)}
        data-analytics-id="case:pms:candidate:confirm"
      >
        Confirm
      </Button>
    </div>
  );
}
